import { useMutation } from "@apollo/client/react";
import { Flex, Space, Typography } from "antd";
import { useCallback, useId, useRef } from "react";
import { useEnterToNext } from "../../../hooks/useEnterToNext.ts";
import { useTranslation } from "react-i18next";
import { UpdateIngredientDocument, type UpdateIngredientInput } from "../../../gql/generated/graphqlSchema.tsx";
import { AllergenMultiSelect } from "../../../components/AllergenMultiSelect.tsx";
import { AutoSaveInput, AutoSaveTextArea } from "../../../components/AutoSaveInput.tsx";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { useIngredient } from "../IngredientContext.tsx";
import { formWide } from "../../../styles/shared.styles.ts";

export const IngredientGeneralTab = () => {
  const { t } = useTranslation();
  const { ingredient, setIngredient } = useIngredient();
  const [updateIngredient] = useMutation(UpdateIngredientDocument);
  const { trackSaving } = useSaving();
  const id = useId();
  const formRef = useRef<HTMLDivElement>(null);
  const enterToNext = useEnterToNext(formRef);

  const saveField = useCallback(
    (input: UpdateIngredientInput) => {
      trackSaving(() =>
        updateIngredient({
          variables: { id: ingredient.id, input },
        }).then((result) => setIngredient(result.data?.updateIngredient.refreshedIngredient)),
      );
    },
    [ingredient.id, updateIngredient, trackSaving, setIngredient],
  );

  return (
    <Flex className={formWide} gap="middle" ref={formRef} vertical>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-name`}>{t("ingredients.name")}</label>
        </Typography.Text>
        <AutoSaveInput
          classNames={{ input: "marker" }}
          defaultValue={ingredient.name}
          id={`${id}-name`}
          onCommit={(v) => saveField({ name: v })}
          onPressEnter={enterToNext}
          placeholder={t("ingredients.namePlaceholder")}
          showCaseToggle
        />
      </Space>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-nameForLabel`}>{t("ingredients.nameForLabel")}</label>
        </Typography.Text>
        <AutoSaveInput
          classNames={{ input: "marker" }}
          defaultValue={ingredient.nameForLabel ?? ""}
          id={`${id}-nameForLabel`}
          onCommit={(v) => saveField({ nameForLabel: v || null })}
          onPressEnter={enterToNext}
          placeholder={ingredient.name}
          showCaseToggle
        />
        <Typography.Text type="secondary">{t("ingredients.nameForLabelHelp")}</Typography.Text>
      </Space>
      <Flex gap={4} vertical>
        <Typography.Text strong>
          <label htmlFor={`${id}-allergens`}>{t("ingredients.allergens")}</label>
        </Typography.Text>
        <AllergenMultiSelect
          id={`${id}-allergens`}
          onChange={(next) => saveField({ allergens: next })}
          placeholder={t("ingredients.allergensPlaceholder")}
          value={ingredient.allergens}
        />
        <Typography.Text type="secondary">{t("ingredients.allergensHelp")}</Typography.Text>
      </Flex>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-brand`}>{t("ingredients.brand")}</label>
        </Typography.Text>
        <AutoSaveInput
          classNames={{ input: "marker" }}
          defaultValue={ingredient.brand ?? ""}
          id={`${id}-brand`}
          onCommit={(v) => saveField({ brand: v || null })}
          onPressEnter={enterToNext}
          showCaseToggle
        />
      </Space>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-desc`}>{t("ingredients.description")}</label>
        </Typography.Text>
        <AutoSaveTextArea
          classNames={{ textarea: "marker" }}
          defaultValue={ingredient.description ?? ""}
          id={`${id}-desc`}
          onCommit={(v) => saveField({ description: v || null })}
          rows={4}
        />
      </Space>
    </Flex>
  );
};
